import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import pLimit from 'p-limit'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import axios, { pluckData } from 'src/utilities/http-client'
import { QuestionData, QuizQuestionV2, QuizV2 } from 'src/v3/core/types'
import Loading from 'src/v3/components/loading'
import AddIcon from '@mui/icons-material/Add'
import {
  CloseIcon,
  CloudUploadIcon,
  ConfirmIcon,
  CorrectIcon,
  DeleteIcon,
  DragAndDropIcon,
  EditIcon,
  ReorderIcon,
} from '../../core/icons'
import { AsyncThunk } from '@reduxjs/toolkit'
import ImageUpload from 'src/v3/components/upload-image'

function moveItem<T>(list: T[], oldIndex: number, newIndex: number) {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

type Answer = {
  id: string
  text: string
  correct: boolean
}

type Part = {
  etag: string
  partNumber: number
}

export let QuizBuilder = ({
  quiz,
  fetchQuiz,
  fetchQuizBy,
  questionData,
  fetchQuestionData,
  apiPath,
  submitButton,
}: {
  quiz: QuizV2
  fetchQuiz: AsyncThunk<QuizV2, string, {}>
  fetchQuizBy: string
  questionData: QuestionData[]
  fetchQuestionData: AsyncThunk<QuestionData[], string, {}>
  apiPath: string
  submitButton: ReactElement
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [questionOrder, setQuestionOrder] = useState<string[]>([])
  let [questionImage, setQuestionImage] = useState<File | undefined>(undefined)
  let [questionVideo, setQuestionVideo] = useState<File | undefined>(undefined)
  let [uploading, setUploading] = useState<number>(0)
  let [imageOrVideo, setImageOrVideo] = useState<'image' | 'video'>('image')
  let [answers, setAnswers] = useState<Answer[]>([])
  let [answerErrors, setAnswerErrors] = useState<string[]>([])
  let [open, setOpen] = useState<boolean>(false)
  let [reorder, setReorder] = useState<boolean>(false)
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let questions = questionData?.map((qd) => qd.quizQuestion) || []

  let name = 'quiz-question-video'

  let orderedQuestions: QuizQuestionV2[] = questionOrder
    .map((id) => questions.find((q) => q.id === id)!)
    .filter((q) => !!q)

  useEffect(() => {
    setQuestionOrder(
      [...(questions || [])]
        ?.sort((a, b) => a?.sequence - b?.sequence)
        ?.map((q) => q.id),
    )
  }, [questionData])

  let form = useFormik({
    validationSchema: yup.object({
      id: yup.string(),
      passingScore: yup
        .number()
        .when(
          ['randomizeQuestions', 'displayedQuestions'],
          (randomizeQuestions, displayedQuestions) =>
            yup
              .number()
              .required()
              .min(1)
              .max(randomizeQuestions ? displayedQuestions : questions.length),
        ),
      displayedQuestions: yup
        .number()
        .when(['randomizeQuestions'], (randomizeQuestions) =>
          randomizeQuestions
            ? yup.number().required().min(1).max(questions.length)
            : yup.number(),
        ),
      randomizeQuestions: yup.bool().required(),
    }),
    initialValues: {
      id: quiz.id,
      passingScore: quiz.passingScore,
      displayedQuestions: quiz.displayedQuestions,
      randomizeQuestions: quiz.randomizeQuestions,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      setLoading(true)
      axios
        .put(`${apiPath}/quiz`, {
          id: values.id,
          passingScore: values.passingScore,
          displayedQuestions: values.randomizeQuestions
            ? values.displayedQuestions
            : questions.length,
          randomizeQuestions: values.randomizeQuestions,
        })
        .catch((err) =>
          setErrorMessage(err?.response?.data || 'Quiz update failed'),
        )
        .then(() =>
          axios.put(`${apiPath}/quiz/question/sequence`, {
            quizID: quiz.id,
            questions: questionOrder,
          }),
        )
        .catch((err) =>
          setErrorMessage(err?.response?.data || 'Sequence update failed'),
        )
        .then(() => dispatch(fetchQuiz(fetchQuizBy)))
        .then(() => dispatch(fetchQuestionData(quiz.id)))
        .then(() => setReorder(false))
        .finally(() => setLoading(false))
    },
  })

  let questionForm = useFormik({
    validationSchema: yup.object({
      id: yup.string(),
      text: yup.string().required(),
      sequence: yup.number().required().min(0),
      explanation: yup.string().required(),
      imageName: yup.string(),
      videoName: yup.string(),
    }),
    initialValues: {
      id: '',
      text: '',
      sequence: (questions?.length ?? 0) + 1,
      explanation: '',
      imageName: '',
      videoName: '',
    },
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      let errMsg = ''
      if (answers.length < 2) {
        errMsg = 'At least two answers are required.'
      } else if (!answers.some((a) => a.correct)) {
        errMsg = 'One answer must be marked correct.'
      }
      let answersText = answers.map((a) => a.text)
      let errors = answers
        .filter(
          (a, i) =>
            !a.text || answersText.some((at, j) => at === a.text && i !== j),
        )
        .map((a) => a.id)

      setAnswerErrors(errors)
      setErrorMessage(errMsg)
      if (errors.length || errMsg) return

      setLoading(true)
      setUploading(imageOrVideo === 'video' && questionVideo ? 0.1 : 0)
      let request = !values.id
        ? axios.post(`${apiPath}/quiz/question`, {
            quizID: quiz.id,
            text: values.text,
            sequence: values.sequence,
            explanation: values.explanation,
            answers: answers.map((a) => ({
              ...a,
              id: a.id.slice(0, 5) === 'temp-' ? undefined : a.id,
            })),
          })
        : axios.put(`${apiPath}/quiz/question`, {
            id: values.id,
            text: values.text,
            sequence: values.sequence,
            explanation: values.explanation,
            answers: answers.map((a) => ({
              ...a,
              id: a.id.slice(0, 5) === 'temp-' ? undefined : a.id,
            })),
          })
      request
        .then(pluckData)
        .then(
          (question: QuizQuestionV2): Promise<any> =>
            imageOrVideo === 'image' && questionImage
              ? axios
                  .put(`${apiPath}/quiz/question/image`, { id: question.id })
                  .then((response) =>
                    axios.put(response.data, questionImage, {
                      headers: {
                        'Cache-Control': 'max-age=2592000',
                        'content-type': 'image/jpeg',
                      },
                    }),
                  )
              : imageOrVideo === 'video' && questionVideo
                ? handleUpload(question)
                : Promise.resolve(),
        )
        .then(() => dispatch(fetchQuiz(fetchQuizBy)))
        .then(() => dispatch(fetchQuestionData(quiz.id)))
        .then(() => {
          setAnswers([])
          setAnswerErrors([])
          setQuestionImage(undefined)
          setQuestionVideo(undefined)
          setOpen(false)
          resetForm()
        })
        .catch((err) =>
          setErrorMessage(
            err?.response?.data ||
              `${!values.id ? 'Create' : 'Update'} question failed`,
          ),
        )
        .finally(() => {
          setLoading(false)
          setUploading(0)
        })
    },
  })

  let getPresignedUrls = async (numberOfParts: number) => {
    let response = await axios.get(
      `/api/video-multipart-urls?numberOfParts=${numberOfParts}`,
    )
    return response.data
  }

  let uploadPart = async (presignedUrl: string, part: Blob) => {
    let response = await axios.put(presignedUrl, part, {
      headers: {
        'Cache-Control': 'max-age=2592000',
        'content-type': 'video/mp4',
      },
    })
    return response.headers
  }

  let completeMultipartUpload = async (
    key: string,
    uploadId: string,
    parts: Part[],
  ) => {
    await axios.post('/api/complete-video-multipart-upload', {
      key,
      uploadId,
      parts,
    })
  }

  let handleUpload = async (question: QuizQuestionV2) => {
    if (!questionVideo) return

    let numberOfParts = Math.floor(questionVideo!.size / 5242880) || 1
    let { signedURLs, uploadID, key } = await getPresignedUrls(numberOfParts)

    let partSize = Math.ceil(questionVideo!.size / numberOfParts)
    let parts: Part[] = []
    let completedParts = 0

    let uploadPartWithProgress = async (
      url: string,
      part: Blob,
      partNumber: number,
    ) => {
      let response = await uploadPart(url, part)
      // eslint-disable-next-line fp/no-mutating-methods
      parts.push({ etag: response.etag, partNumber })
      // eslint-disable-next-line fp/no-mutation
      completedParts += 1
      setUploading((100 * completedParts) / (numberOfParts + 1))
    }

    let limit = pLimit(6)

    let uploadPromises = Array.from({ length: numberOfParts }, (_, i) => {
      let part = questionVideo!.slice(i * partSize, (i + 1) * partSize)
      return limit(() => uploadPartWithProgress(signedURLs[i], part!, i + 1))
    })

    await Promise.all(uploadPromises)

    await completeMultipartUpload(key, uploadID, parts)

    await axios.put(`${apiPath}/quiz/question/video`, {
      id: question.id,
      videoName: key,
    })

    setUploading(100)
  }

  let VideoUpload = () => (
    <>
      <input
        accept="video/mp4"
        style={{ display: 'none' }}
        id={name}
        multiple
        type="file"
        onChange={(e: React.FormEvent<HTMLInputElement>) => {
          setQuestionVideo(e.currentTarget.files?.[0] || undefined)
        }}
      />
      <Box
        sx={{
          borderWidth: 2,
          borderStyle: 'dashed',
          borderColor: 'background.paper',
          borderRadius: 2,
          bgcolor: '#0b0b0b',
          px: 2,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CloudUploadIcon sx={{ color: 'grey', height: 40, width: 60 }} />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 1, mb: 0.5 }}
        >
          Drag and drop your video here, or
        </Typography>
        <label style={{ marginTop: 0 }} htmlFor={name}>
          <Button
            component="span"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            browse files
          </Button>
        </label>
        {(questionForm.values.videoName || questionVideo) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.5,
              mb: 1,
            }}
          >
            <CorrectIcon color="success" />
            <Typography>
              {questionVideo?.name || questionForm.values.videoName}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  )

  return (
    <>
      <form onSubmit={form.handleSubmit}>
        <Card
          variant="outlined"
          sx={{ mb: 2, p: 2, bgcolor: 'background.paperDark' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{ alignSelf: 'center', ml: 0.5, fontWeight: 'bold' }}
            >
              Questions
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {questionOrder.length > 1 && (
                <Tooltip title="Reorder questions">
                  <IconButton
                    color="primary"
                    onClick={() => setReorder(!reorder)}
                  >
                    <ReorderIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ textTransform: 'none' }}
                onClick={() => setOpen(true)}
              >
                Add Question
              </Button>
            </Box>
          </Box>
          {reorder ? (
            <Box>
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) {
                    return
                  }
                  setQuestionOrder(
                    moveItem(
                      questionOrder,
                      result.source.index,
                      result.destination?.index,
                    ),
                  )
                }}
              >
                <Droppable droppableId="questions">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {orderedQuestions?.map((question, i) => (
                        <Draggable
                          key={question.id}
                          draggableId={question.id || i.toString()}
                          index={i}
                        >
                          {(prov) => (
                            <Card
                              sx={{ p: 2, mt: 2, display: 'flex', gap: 2 }}
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                            >
                              <DragAndDropIcon />
                              <Typography>{question.text}</Typography>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          ) : (
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {orderedQuestions?.map((question, i) => {
                let data = questionData.find(
                  (qd) => qd.quizQuestion.id === question.id,
                )
                let correct = data?.correctAnswer
                let answers = data?.questionAnswers.map((a) => ({
                  id: a.id,
                  text: a.text,
                  correct: a.id === correct?.answerID,
                }))
                return (
                  <Paper
                    key={question.id}
                    sx={{ p: 2, bgcolor: 'background.paperDark' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography>Question {i + 1}</Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            questionForm.setFieldValue('id', question.id)
                            questionForm.setFieldValue('text', question.text)
                            questionForm.setFieldValue(
                              'sequence',
                              question.sequence,
                            )
                            questionForm.setFieldValue(
                              'explanation',
                              question.explanation,
                            )
                            questionForm.setFieldValue(
                              'imageName',
                              question.imageName || '',
                            )
                            questionForm.setFieldValue(
                              'videoName',
                              question.videoName || '',
                            )

                            let data = questionData.find(
                              (qd) => qd.quizQuestion.id === question.id,
                            )
                            let correct = data?.correctAnswer
                            let ans =
                              data?.questionAnswers.map((a) => ({
                                id: a.id,
                                text: a.text,
                                correct: a.id === correct?.answerID,
                              })) || []
                            setAnswers(ans)

                            setImageOrVideo(
                              question.videoName ? 'video' : 'image',
                            )
                            setOpen(true)
                          }}
                        >
                          <EditIcon sx={{ height: 20, width: 20 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setLoading(true)
                            axios
                              .delete(
                                `${apiPath}/quiz/question?questionID=${question.id}`,
                              )
                              .then(() => dispatch(fetchQuiz(fetchQuizBy)))
                              .then(() => dispatch(fetchQuestionData(quiz.id)))
                              .catch((err) =>
                                setErrorMessage(
                                  err?.response?.data ||
                                    'Delete question failed',
                                ),
                              )
                              .finally(() => setLoading(false))
                          }}
                        >
                          <DeleteIcon sx={{ height: 20, width: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography sx={{ my: 1 }}>{question.text}</Typography>
                    {answers?.map((a) => (
                      <Box key={a.id} sx={{ display: 'flex', gap: 1 }}>
                        {a.id === correct?.answerID ? (
                          <ConfirmIcon color="success" />
                        ) : (
                          <CloseIcon color="error" />
                        )}
                        <Typography
                          color={
                            a.id === correct?.answerID
                              ? 'textPrimary'
                              : 'textSecondary'
                          }
                        >
                          {a.text}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )
              })}
            </Box>
          )}
        </Card>
        <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.paperDark' }}>
          <Typography>Quiz Settings</Typography>
          <FormControlLabel
            label="Randomize Questions"
            control={
              <Checkbox
                checked={form.values.randomizeQuestions}
                value={form.values.randomizeQuestions}
                name="randomizeQuestions"
                onChange={form.handleChange}
              />
            }
          />
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {form.values.randomizeQuestions && (
              <Box sx={{ flexBasis: '160px', flexGrow: 1 }}>
                <TextField
                  sx={{ bgcolor: 'background.paperDark', mb: 0.5 }}
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={form.values.displayedQuestions}
                  label="Displayed Questions"
                  name="displayedQuestions"
                  onChange={form.handleChange}
                  error={
                    form.touched.displayedQuestions &&
                    !!form.errors.displayedQuestions
                  }
                />
                <Typography variant="subtitle2" color="textSecondary">
                  Select how many of this quiz's {questions.length} questions
                  will be randomly selected when taken by users.
                </Typography>
              </Box>
            )}
            <Box sx={{ flexBasis: '160px', flexGrow: 1 }}>
              <TextField
                sx={{ bgcolor: 'background.paperDark', mb: 0.5 }}
                size="small"
                fullWidth
                variant="outlined"
                value={form.values.passingScore}
                label="Passing Score"
                name="passingScore"
                onChange={form.handleChange}
                error={form.touched.passingScore && !!form.errors.passingScore}
              />
              <Typography variant="subtitle2" color="textSecondary">
                Select how many of this quiz's {questions.length} displayed
                questions must be answered correctly for a user to pass the
                quiz.
              </Typography>
            </Box>
          </Box>
        </Card>
        {submitButton}
        <Loading entities={[]} loading={loading && !uploading} />
        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </form>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          bgcolor: 'black',
          '& .MuiDrawer-paper': {
            bgcolor: 'black',
            width: '100%',
          },
        }}
      >
        <Box sx={{ bgcolor: 'black', p: 4, pt: 14 }}>
          <form onSubmit={questionForm.handleSubmit}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}
            >
              <Typography variant="h6">Add Question</Typography>
              <IconButton
                onClick={() => {
                  setAnswers([])
                  setAnswerErrors([])
                  setQuestionImage(undefined)
                  setOpen(false)
                  questionForm.resetForm()
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <TextField
              sx={{ bgcolor: 'background.paperDark', my: 1 }}
              size="small"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              value={questionForm.values.text}
              label="Question Text"
              name="text"
              onChange={questionForm.handleChange}
              error={questionForm.touched.text && !!questionForm.errors.text}
            />
            <Typography variant="subtitle1">
              Add Image or Video (optional)
            </Typography>
            <ToggleButtonGroup
              sx={{ my: 0.5 }}
              size="small"
              color="primary"
              value={imageOrVideo}
              exclusive
              onChange={(_, val) => setImageOrVideo(val)}
            >
              <ToggleButton value="image" sx={{ px: 2 }}>
                Image
              </ToggleButton>
              <ToggleButton value="video" sx={{ px: 2 }}>
                Video
              </ToggleButton>
            </ToggleButtonGroup>
            {imageOrVideo === 'image' ? (
              <ImageUpload
                type="default"
                image={questionImage}
                setImage={setQuestionImage}
                existingImage={questionForm.values.imageName}
              />
            ) : (
              <VideoUpload />
            )}
            <Typography sx={{ mt: 2 }}>Answers</Typography>
            <Typography variant="subtitle2">
              Use the buttons to the left to rearrange answers and to indicate
              which one is correct.
            </Typography>
            <Box>
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) {
                    return
                  }
                  setAnswers(
                    moveItem(
                      answers,
                      result.source.index,
                      result.destination?.index,
                    ),
                  )
                }}
              >
                <Droppable droppableId="answers">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {answers.map((answer, i) => (
                        <Draggable
                          key={answer.id}
                          draggableId={answer.text + i || i.toString()}
                          index={i}
                        >
                          {(prov) => (
                            <Box
                              sx={{
                                mt: 2,
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                              }}
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                            >
                              <DragAndDropIcon />
                              <IconButton
                                onClick={() =>
                                  setAnswers(
                                    answers.map((answer, j) => ({
                                      ...answer,
                                      correct: i === j,
                                    })),
                                  )
                                }
                              >
                                {answer.correct ? (
                                  <ConfirmIcon color="success" />
                                ) : (
                                  <CloseIcon color="error" />
                                )}
                              </IconButton>
                              <TextField
                                sx={{ bgcolor: 'background.paperDark' }}
                                size="small"
                                fullWidth
                                variant="outlined"
                                value={answer.text}
                                label={`Answer ${i + 1}`}
                                name="text"
                                onChange={(e) => {
                                  setAnswerErrors(
                                    answerErrors.filter(
                                      (ae) => ae !== answer.id,
                                    ),
                                  )
                                  setAnswers(
                                    answers.map((answer, j) =>
                                      i === j
                                        ? { ...answer, text: e.target.value }
                                        : answer,
                                    ),
                                  )
                                }}
                                error={answerErrors.includes(answer.id)}
                              />
                              <IconButton
                                onClick={() => {
                                  setAnswers(answers.filter((_, j) => j !== i))
                                  setAnswerErrors([])
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
            {answers.length < 10 && (
              <Button
                sx={{ textTransform: 'none', mt: 1 }}
                startIcon={<AddIcon />}
                onClick={() => {
                  setAnswers([
                    ...answers,
                    {
                      id: `temp-${Date.now().toString()}`,
                      text: '',
                      correct: !answers.length,
                    },
                  ])
                }}
              >
                Add Answer
              </Button>
            )}
            <TextField
              sx={{ bgcolor: 'background.paperDark', mt: 2 }}
              size="small"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              value={questionForm.values.explanation}
              label="Answer Explanation"
              name="explanation"
              onChange={questionForm.handleChange}
              error={
                questionForm.touched.explanation &&
                !!questionForm.errors.explanation
              }
            />
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                sx={{
                  color: 'text.primary',
                  borderColor: 'background.paper',
                  textTransform: 'none',
                }}
                variant="outlined"
                onClick={() => {
                  questionForm.resetForm()
                  setAnswers([])
                  setAnswerErrors([])
                  setQuestionImage(undefined)
                  setQuestionVideo(undefined)
                  setOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                sx={{ textTransform: 'none' }}
                type="submit"
                variant="contained"
                loading={loading}
              >
                Save Question
              </Button>
            </Box>
            {uploading > 0 && (
              <Box>
                <Typography sx={{ mt: 1, mb: 0.5 }}>
                  Your video is uploading. This will take a moment.
                </Typography>
                <LinearProgress variant="determinate" value={uploading} />
              </Box>
            )}
          </form>
        </Box>
      </Drawer>
    </>
  )
}
